import { ThemeProvider } from '@mui/material/styles'
import { useAuthentication, useCountries, useDictionary, countryState, customerState, languageState } from '@repo/unicity'
import { useAtom, useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import InAppSpy from 'inapp-spy'
import { theme } from './theme/theme'
import WebFont from 'webfontloader'
import AppRouter from './components/core/router/AppRouter'
import Loader from './components/core/loader/Loader'
import { useJapanCountry } from './hooks/useJapanCountry'
import { useSpotlightSsoEntry } from './hooks/useSpotlightSsoEntry'
import './App.css'

import { FontLoader } from '@repo/fonts'

// Module-level flags to ensure init runs only once (prevents double execution in StrictMode)
let didInit = false
let didFetchDictionary = false
let hasOpenedExternalBrowser = false

function openInBrowser() {
    if (hasOpenedExternalBrowser) return
    hasOpenedExternalBrowser = true

    const url = window.location.href
    const isAndroid = /Android/i.test(navigator.userAgent)
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname)
    const isIntentUrl = url.startsWith('intent://')

    if (isLocalhost) return

    if (isAndroid && !isIntentUrl) {
        window.location.href = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;end`
        return
    }

    if (isIOS) {
        window.open(url, '_system')
        return
    }

    window.open(url, '_blank')
}

function App() {
    const [{ isInApp }] = useState(() => InAppSpy())
    const { fetchCountries } = useCountries()
    const country = useAtomValue(countryState)
    const customer = useAtomValue(customerState)
    const { authenSso, loading } = useAuthentication({ enablePersist: true })
    const [, setLanguage] = useAtom(languageState)
    const [fontsLoaded, setFontsLoaded] = useState(false)
    const [isReady, setIsReady] = useState(false)

    const { getDictionary } = useDictionary()

    // Initialize Japan country (hardcoded for Spotlight app)
    const { isLoading: isLoadingCountry } = useJapanCountry()
    useSpotlightSsoEntry()

    useEffect(() => {
        if (isInApp) openInBrowser()
    }, [isInApp])

    // Init once pattern - prevents double execution in StrictMode
    useEffect(() => {
        if (didInit) return
        didInit = true
        fetchCountries()
        authenSso()
    }, [fetchCountries, authenSso])

    // Dictionary API - fetch only once when country.short is available
    // Uses module-level flag to prevent double calls in StrictMode and re-renders
    useEffect(() => {
        if (didFetchDictionary) return
        if (!country.short) return

        didFetchDictionary = true
        getDictionary({ countryCode: country.short, web: 'feelGreat' })
    }, [country.short, getDictionary])

    // Set page title and language from URL - use primitive dependencies
    const countryShorter = country.shorter
    const languageItems = country.config?.languageSettings?.items
    useEffect(() => {
        if (!countryShorter) return
        document.title = `Unicity (${countryShorter})`
        const langParam = new URLSearchParams(window.location.search).get('lang')
        if (langParam && languageItems?.find(item => item.code === langParam)) {
            setLanguage(langParam)
        }
    }, [countryShorter, languageItems, setLanguage])

    // Load web fonts once
    useEffect(() => {
        WebFont.load({
            custom: {
                families: ['Inter:300,400,500,600,700', 'Poppins:300,400,500,600,700', 'NotoSansThai:300,400,500,600,700']
            },
            active: () => setFontsLoaded(true)
        })
    }, [])

    // Derive ready state - use primitive dependencies
    const customerId = customer.id?.unicity
    useEffect(() => {
        if (customerId && !loading && fontsLoaded && !isLoadingCountry && countryShorter) {
            setIsReady(true)
        }
    }, [customerId, loading, fontsLoaded, isLoadingCountry, countryShorter])

    return (
        <ThemeProvider theme={theme}>
            <FontLoader fonts={['inter', 'poppins']} />
            {isReady ? <AppRouter /> : <Loader />}
        </ThemeProvider>
    )
}

export default App
